import { Component, useEffect, type ReactNode, type ErrorInfo } from 'react'

import { isChunkLoadError, attemptChunkAutoReload } from './chunkError'
import { useRouteError, isRouteErrorResponse } from 'react-router-dom'
import { queryClient } from '@/lib/queryClient'

interface State {
  error: Error | null
}

// ── Chunk-load failure handling ──────────────────────────────────────────
//
// After a `kubectl set image` deploy, a browser tab that was already open
// (or navigating from a bfcache/stale index.html) can request a JS chunk
// filename that no longer exists on the server — there is no cache-busting
// deploy step here. Vite/Rolldown's dynamic import() then rejects, which
// React surfaces as a thrown render error (named 'ChunkLoadError' in
// webpack-style bundlers; this project's Rolldown/Vite output throws a
// TypeError whose message contains "Failed to fetch dynamically imported
// module" — both are treated as the same failure class here). Without this
// handling, that error would fall through to whichever boundary catches it
// with the generic "Something went wrong" UI, or — for the 8 public routes
// below that have no errorElement/boundary at all — react-router's bare
// default error page.




function ChunkErrorContent() {
  return (
    <div className="flex h-64 flex-col items-center justify-center gap-4">
      <div className="text-center">
        <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
          A new version of this app is available.
        </p>
        <p className="mt-1 text-xs text-gray-500">
          This page couldn't load the latest update. Reload to continue.
        </p>
      </div>
      <button
        onClick={() => window.location.reload()}
        className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
      >
        Reload
      </button>
    </div>
  )
}

/**
 * Functional component for data-router errorElement — uses useRouteError()
 * to surface loader/action errors (thrown responses, Error objects, strings)
 * as well as render errors (including a lazy() chunk import rejecting).
 * Chunk-load failures get a distinct message, an explicit Reload button, and
 * a one-shot automatic reload (see attemptChunkAutoReload above).
 */
export function RouteErrorElement() {
  const error = useRouteError()
  const chunkError = isChunkLoadError(error)

  useEffect(() => {
    if (chunkError) {
      attemptChunkAutoReload()
    }
  }, [chunkError])

  if (chunkError) {
    return <ChunkErrorContent />
  }

  let message = 'An unexpected error occurred.'
  if (isRouteErrorResponse(error)) {
    message = `${error.status} ${error.statusText || error.data}`
  } else if (error instanceof Error) {
    message = error.message
  } else if (typeof error === 'string') {
    message = error
  }
  return (
    <div className="flex h-64 flex-col items-center justify-center gap-4">
      <div className="text-center">
        <p className="text-sm font-medium text-red-600 dark:text-red-400">Something went wrong loading this page.</p>
        <p className="mt-1 text-xs text-gray-500">{message}</p>
      </div>
      <a
        href="/"
        className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
      >
        Go home
      </a>
    </div>
  )
}

/**
 * Error boundary that wraps the main content area.
 * Catches render errors without losing the sidebar/header chrome. This is
 * the boundary that actually catches a lazy() chunk-load failure for an
 * authenticated child route (it sits inside Layout, closer to the throw
 * site than the router-level errorElement above) — so it gets the same
 * chunk-aware handling.
 */
export class RouteErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[RouteErrorBoundary]', error, info.componentStack)
    if (isChunkLoadError(error)) {
      attemptChunkAutoReload()
    }
  }

  render() {
    if (this.state.error) {
      if (isChunkLoadError(this.state.error)) {
        return <ChunkErrorContent />
      }
      return (
        <div className="flex h-64 flex-col items-center justify-center gap-4">
          <div className="text-center">
            <p className="text-sm font-medium text-red-600 dark:text-red-400">Something went wrong loading this page.</p>
            <p className="mt-1 text-xs text-gray-500">{this.state.error.message}</p>
          </div>
          <button
            onClick={() => {
              queryClient.resetQueries()
              this.setState({ error: null })
            }}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Try again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
